import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useGetInsurancesQuery, useDeleteInsuranceMutation } from '../../slices/insurancesApiSlice';
import { useGetOpeningStocksQuery } from '../../slices/openingStocksApiSlice'; // For qty and itemCost
import { useGetSuppliesQuery } from '../../slices/suppliesApiSlice'; // For Pqty and Pcost
import { toast } from 'react-toastify';
import { useState } from 'react';
import * as XLSX from "xlsx";

const InsuranceListScreen = () => {
  const [filter, setFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: insurancesData, isLoading, error, refetch } = useGetInsurancesQuery(pageNumber);
  const { data: openingStockData } = useGetOpeningStocksQuery();
  const { data: suppliesData } = useGetSuppliesQuery();

  const [deleteInsurance, { isLoading: loadingDelete }] = useDeleteInsuranceMutation();

  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this insurance?')) {
      try {
        await deleteInsurance(id).unwrap();
        toast.success('Insurance deleted successfully!');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete insurance.');
      }
    }
  };

  const filterInsurances = () => {
    if (!insurancesData?.insurances) return [];
    return insurancesData.insurances.filter((insurance) => {
      const insuranceDate = new Date(insurance.date);
      const isWithinDateRange =
        (!startDate || insuranceDate >= new Date(startDate)) &&
        (!endDate || insuranceDate <= new Date(endDate));
      return (
        isWithinDateRange &&
        (insurance.note?.toLowerCase().includes(filter.toLowerCase()) ||
          insurance.customerDetails?.name?.toLowerCase().includes(filter.toLowerCase()) ||
          insurance.customerDetails?.company?.toLowerCase().includes(filter.toLowerCase()) ||
          insurance.customerDetails?.address?.toLowerCase().includes(filter.toLowerCase()) ||
          insurance.insuranceItems.some((item) =>
            item.name.toLowerCase().includes(filter.toLowerCase())
          ))
      );
    });
  };

  const calculatePqty = (productName) => {
    return suppliesData?.supplies
      ?.reduce((totalQty, supply) => {
        const productItems = supply.supplyItems.filter((item) => item.name === productName);
        const productQty = productItems.reduce((qty, item) => qty + item.qty, 0);
        return totalQty + productQty;
      }, 0) || 0;
  };

  const calculatePcost = (productName) => {
    return suppliesData?.supplies
      ?.reduce((totalCost, supply) => {
        const productItems = supply.supplyItems.filter((item) => item.name === productName);
        const productCost = productItems.reduce((cost, item) => cost + item.itemCost, 0);
        return totalCost + productCost;
      }, 0) || 0;
  };

  const calculateAverageCost = (productName) => {
    const openingStockItems = openingStockData?.openingStocks
      ?.flatMap((openingStock) => openingStock.openingStockItems)
      ?.filter((item) => item.name === productName);

    const qty = openingStockItems?.reduce((total, item) => total + item.qty, 0) || 0;
    const itemCost = openingStockItems?.reduce((total, item) => total + item.itemCost, 0) || 0;

    const Pqty = calculatePqty(productName);
    const Pcost = calculatePcost(productName);

    const totalQty = qty + Pqty;
    const totalCost = itemCost + Pcost;

    return totalQty > 0 ? totalCost / totalQty : 0;
  };

  const calculateTotals = () => {
    const totalInsurances = filterInsurances().reduce(
      (total, insurance) =>
        total +
        insurance.insuranceItems.reduce(
          (subTotal, item) => subTotal + item.qty * item.price,
          0
        ),
      0
    );

    const totalCost = filterInsurances().reduce((total, insurance) => {
      return total + insurance.insuranceItems.reduce((subTotal, item) => {
        const averageCost = calculateAverageCost(item.name);
        return subTotal + averageCost * item.qty;
      }, 0);
    }, 0);

    const totalProfit = totalInsurances - totalCost;

    return { totalInsurances, totalCost, totalProfit };
  };

  const { totalInsurances, totalCost, totalProfit } = calculateTotals();

  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  const exportTableToExcel = () => {
    const tableData = filterInsurances().map((insurance) =>
      insurance.insuranceItems.map((item) => {
        const averageCost = calculateAverageCost(item.name);
        const totalCost = averageCost * item.qty;
        const profit = item.qty * item.price - totalCost;
  
        return {
          Date: new Date(insurance.date).toLocaleDateString(),
          Product: item.name,
          Qty: item.qty,
          Price: item.price.toFixed(2),
          Total: (item.qty * item.price).toFixed(2),
          GTotal: (item.qty * item.price * 1.4).toFixed(0),
          
          TotalCost: totalCost.toFixed(2),
          Profit: profit.toFixed(2),
          Note: insurance.note || "N/A",
          Customer: insurance.customerDetails?.name || "N/A",
          Company: insurance.customerDetails?.company || "N/A",
          Telephone: insurance.customerDetails?.telephone || "N/A",
          Serial: insurance.customerDetails?.serialNumber || "N/A",
          Address: insurance.customerDetails?.address || "N/A",
        };
      })
    ).flat();
  
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Insurances Table Export");
    XLSX.writeFile(workbook, "InsurancesTable.xlsx");
  };

  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <h1>Insurances</h1>
          <Button variant="success" onClick={exportTableToExcel}>Export Table to Excel</Button>
        </Col>
        
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {/* Totals Section */}
      <Row className="mb-3">
        <Col md={4}>
          <h4>Total Insurances: ${totalInsurances.toFixed(2)}</h4>
          <h4>GTotal : ${(totalInsurances * 1.4).toFixed(2)}</h4>
        </Col>
        <Col md={4}>
          <h4>Total Cost: ${totalCost.toFixed(2)}</h4>
        </Col>
        <Col md={4} className="text-end">
          <h4>Total Profit: ${totalProfit.toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>GTOTAL</th>
                <th>CUSTOMER</th>
                <th>COMPANY</th>
                <th>SERIAL</th>
                <th>SUBCOM</th>
                <th>TEL</th>
                <th>NOTE</th>
                
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterInsurances().map((insurance) =>
                insurance.insuranceItems.map((item) => {
                  //const averageCost = calculateAverageCost(item.name);
                  //const totalCost = averageCost * item.qty;
                  //const profit = item.qty * item.price - totalCost;

                  return (
                    <tr key={`${insurance._id}-${item.product}`}>
                      <td>{new Date(insurance.date).toLocaleDateString()}</td>
                      <td>{item.name}</td>
                      <td>{item.qty}</td>
                      <td>{(item.qty * item.price).toFixed(2)}</td>
                      <td>{(item.qty * item.price * 1.4).toFixed(0)}</td>
                      <td>{insurance.customerDetails.name || 'N/A'}</td>
                      <td>{insurance.customerDetails.company || 'N/A'}</td>
                      <td>{insurance.customerDetails.serialNumber || 'N/A'}</td>
                      <td>{insurance.customerDetails.address || 'N/A'}</td>
                      <td>{insurance.customerDetails.telephone || 'N/A'}</td>
                      <td>{insurance.note || 'N/A'}</td>
                      
                      <td  style={{display: 'none'}}>
                        {item.images && (
                          <img
                            src={item.images[0]}
                            alt="Thumbnail"
                            className="thumbnail-img me-2"
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '5px',
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Button
                          as={Link}
                          to={`/insurance/${insurance._id}`}
                          variant="light"
                          className="btn-sm"
                        >
                          Details
                        </Button>
                        <Button
                          as={Link}
                          to={`/admin/insurance/${insurance._id}/edit`}
                          variant="light"
                          className="btn-sm mx-2"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          className="btn-sm mx-2"
                          onClick={() => deleteHandler(insurance._id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(insurancesData.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default InsuranceListScreen;